'use client';

import { useGeoSearchString, useWLMode } from '@/contexts/CookieContext';
import { useGetCompleteDict } from '@/hooks/useGetCompleteDict';
import { usePathname } from '@/navigation';
import { CommonParams, convertParamsToPath, ParameterValue } from '@/utils/SEOPathModule/convertParamsToPath';
import { convertPathToParams } from '@/utils/SEOPathModule/convertPathToParams';
import { Dicts } from '@/utils/SEOPathModule/types';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

const updateCanonicalLink = (href: string) => {
  if (typeof document === 'undefined') return;

  let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }

  const baseHref = canonicalLink.href || window.location.href;

  const setCanonicalSafe = (targetHref: string, fallbackBase: string) => {
    const url = new URL(targetHref, fallbackBase);
    url.search = ''; // drop query params from canonical
    url.hash = ''; // drop fragments from canonical
    canonicalLink!.href = url.toString();
  };

  try {
    setCanonicalSafe(href, baseHref);
  } catch {
    try {
      setCanonicalSafe(href, window.location.origin);
    } catch {
      // noop – keep the existing canonical if parsing fails
    }
  }
};

export function replaceUrl(href: string) {
  // Keep it same-origin and relative
  if (href.startsWith('http')) {
    const u = new URL(href);
    href = u.pathname + u.search + u.hash;
  }
  if (!href.startsWith('/')) href = '/' + href;

  window.history.replaceState(null, '', href); // 👈 no navigation, no server
  updateCanonicalLink(href);
}

/*
 растащить на несколько частей- - это будет хук локального парсинга урла и выдачи состояния
 из него убрать  updateFiltersState - он не будет обновлять урл
 ще убрать гео полностью из отдачи на обновление параметров

 что со словарями - они будут на странице
*/

export const useFiltersState = (initialDicts: Dicts) => {
  const wlMode = useWLMode();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const geoSearchStr = useGeoSearchString();
  const [dicts, setDicts] = useState(initialDicts);
  const [isDictsReady, setIsDictsReady] = useState(false);
  const { data: loadedDicts, isLoading, isError } = useGetCompleteDict();

  const hasLoadedCompleteDicts = loadedDicts !== undefined;

  useEffect(() => {
    if (hasLoadedCompleteDicts) {
      setDicts(loadedDicts);
      setIsDictsReady(true);
    }
  }, [hasLoadedCompleteDicts, loadedDicts]);

  const { geo, params, commonParams, categories, breadcrumbs, hasAnyParamInPath, pathParams } = useMemo(() => {
    const slug = pathname.trim().split('/');
    //remove leading empty string
    slug.shift();
    const converted = convertPathToParams(slug, searchParams, dicts, locale, wlMode);
    return converted;
  }, [searchParams, pathname, locale, wlMode, dicts]);

  const updatePath = useCallback((newPath: string) => {
    replaceUrl(newPath);
  }, []);
  const { categoryIdDict } = dicts;
  const updateGeo = useCallback(
    (geoPath: string) => {
      const newPath = convertParamsToPath(
        {
          parameters: params,
          categories: categories.map((val) => val.id),
          commonParams,
        },
        { categoryIdDict },
        geoPath,
      );

      updatePath(newPath);

      return {
        parameters: params,
        categories: categories.map((val) => val.id),
        commonParams: commonParams,
      };
    },
    [commonParams, params, categories, updatePath, categoryIdDict],
  );

  const updateCategories = useCallback(
    (newCategories: string[]) => {
      const newPath = convertParamsToPath(
        {
          geo,
          parameters: params,
          categories: newCategories,
          commonParams,
        },
        { categoryIdDict },
        geoSearchStr,
      );

      updatePath(newPath);
    },
    [commonParams, geo, params, updatePath, geoSearchStr, categoryIdDict],
  );

  const updateParams = useCallback(
    (newParams: ParameterValue[]) => {
      const newPath = convertParamsToPath(
        {
          geo,
          parameters: newParams,
          categories: categories.map((val) => val.id),
          commonParams,
        },
        { categoryIdDict },
        geoSearchStr,
      );

      updatePath(newPath);
    },
    [commonParams, geo, updatePath, categories, geoSearchStr, categoryIdDict],
  );

  const updateCommonParams = useCallback(
    (newCommonParams: Partial<CommonParams>) => {
      const newPath = convertParamsToPath(
        {
          geo,
          parameters: params,
          categories: categories.map((val) => val.id),
          commonParams: {
            ...commonParams,
            ...newCommonParams,
          },
        },
        { categoryIdDict },
        geoSearchStr,
      );

      updatePath(newPath);
    },
    [commonParams, geo, updatePath, categories, params, geoSearchStr, categoryIdDict],
  );

  const updateFiltersState = useCallback(
    (newCommonParams: Partial<CommonParams>, newParams: ParameterValue[], newCategories: string[]) => {
      const newPath = convertParamsToPath(
        {
          geo,
          parameters: newParams,
          categories: newCategories,
          commonParams: {
            ...commonParams,
            ...newCommonParams,
          },
        },
        { categoryIdDict },
        geoSearchStr,
      );

      updatePath(newPath);
      return {
        geo,
        parameters: newParams,
        categories: newCategories,
        commonParams: {
          ...commonParams,
          ...newCommonParams,
        },
      };
    },
    [geo, commonParams, geoSearchStr, categoryIdDict, updatePath],
  );

  const cancelAllSelectedByNewSearch = useCallback(
    (search?: string) => {
      updateFiltersState(
        {
          skip: undefined,
          searchOnlyInTitle: undefined,
          minPrice: undefined,
          maxPrice: undefined,
          isFree: undefined,
          sortBy: undefined,
          search: search,
        },
        [],
        [],
      );
    },
    [updateFiltersState],
  );

  const isDictsLoading = !isDictsReady && isLoading;
  const isDictsError = !isDictsReady && isError;

  return {
    geo,
    params,
    commonParams,
    categories,
    hasAnyParamInPath,
    pathParams,
    updateParams,
    updateCommonParams,
    updateCategories,
    updateGeo,
    updateFiltersState,
    breadcrumbs,
    cancelAllSelectedByNewSearch,
    isDictsReady,
    isDictsLoading,
    isDictsError,
  };
};
